import React, { useState, useEffect } from 'react';
import esgCustomReportsService from '../../../services/esg/esgCustomReportsService';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

/**
 * ESG Custom Reports Component
 * 
 * Modern report generation and management platform with drag-and-drop builder,
 * template library, scheduling, collaboration, and multi-format export capabilities.
 */
const ESGCustomReports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [reportTemplates, setReportTemplates] = useState([]);
  const [customReports, setCustomReports] = useState([]);
  const [reportBuilder, setReportBuilder] = useState({});
  const [exportOptions, setExportOptions] = useState([]);
  const [reportSchedules, setReportSchedules] = useState([]);
  const [reportAnalytics, setReportAnalytics] = useState({});
  const [collaboration, setCollaboration] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ framework: '', status: '', category: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = () => {
      setReportTemplates(esgCustomReportsService.getReportTemplates());
      setCustomReports(esgCustomReportsService.getCustomReports());
      setReportBuilder(esgCustomReportsService.getReportBuilder());
      setExportOptions(esgCustomReportsService.getExportOptions());
      setReportSchedules(esgCustomReportsService.getReportSchedules());
      setReportAnalytics(esgCustomReportsService.getReportAnalytics());
      setCollaboration(esgCustomReportsService.getCollaboration());
      setLoading(false);
    };

    loadData();
    const unsubscribe = esgCustomReportsService.subscribe(loadData);
    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mb-4"></div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Loading Custom Reports</h3>
          <p className="text-gray-600">Initializing report generation platform...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '📊', color: 'blue' },
    { id: 'templates', name: 'Templates', icon: '📋', color: 'purple' },
    { id: 'my-reports', name: 'My Reports', icon: '📄', color: 'green' },
    { id: 'builder', name: 'Report Builder', icon: '🔧', color: 'orange' },
    { id: 'analytics', name: 'Analytics', icon: '📈', color: 'pink' },
    { id: 'collaboration', name: 'Collaborate', icon: '👥', color: 'indigo' }
  ];

  const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl">
        <div className="container-fluid py-8 px-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-[300px]">
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                  <span className="text-3xl">📊</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold mb-1">Custom Reports</h1>
                  <p className="text-blue-100 text-sm">Dynamic ESG Report Generation & Management Platform</p>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/70 rounded-lg px-4 py-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <span className="absolute left-3 top-2.5 text-white/70">🔍</span>
              </div>
              <button 
                onClick={() => { setModalType('create'); setShowModal(true); }}
                className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-2 rounded-lg text-sm font-semibold transition-all shadow-lg flex items-center gap-2"
              >
                <span>+</span>
                <span>Create Report</span>
              </button>
              <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 border border-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-all">
                📥 Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container-fluid px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-fluid px-6 py-6">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'templates' && renderTemplates()}
        {activeTab === 'my-reports' && renderMyReports()}
        {activeTab === 'builder' && renderBuilder()}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'collaboration' && renderCollaboration()}
      </div>

      {/* Modal */}
      {showModal && renderModal()}
    </div>
  );

  function renderOverview() {
    const stats = [
      { label: 'Total Reports', value: customReports.length, change: '+23%', icon: '📊', color: 'from-blue-500 to-blue-600', trend: 'up' },
      { label: 'Active Templates', value: reportTemplates.filter(t => t.status === 'Active').length, change: '+5', icon: '📋', color: 'from-purple-500 to-purple-600', trend: 'up' },
      { label: 'Scheduled Reports', value: reportSchedules.filter(s => s.status === 'Active').length, change: '8 running', icon: '⏰', color: 'from-green-500 to-green-600', trend: 'stable' },
      { label: 'Success Rate', value: `${reportAnalytics.performance?.successRate || 98}%`, change: 'Excellent', icon: '✓', color: 'from-emerald-500 to-emerald-600', trend: 'up' }
    ];

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className={`bg-gradient-to-br ${stat.color} p-3 rounded-xl shadow-lg`}>
                  <span className="text-2xl">{stat.icon}</span>
                </div>
                <span className={`px-3 py-1 ${
                  stat.trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                } text-xs font-semibold rounded-full`}>
                  {stat.change}
                </span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-gray-600 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Report Generation Trend */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Report Generation Trend</h3>
                <p className="text-sm text-gray-600">Last 7 days activity</p>
              </div>
              <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">↗ +18%</span>
            </div>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={generateActivityData()}>
                  <defs>
                    <linearGradient id="colorReports" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip />
                  <Area type="monotone" dataKey="reports" stroke="#3b82f6" fillOpacity={1} fill="url(#colorReports)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Format Distribution */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Export Format Distribution</h3>
                <p className="text-sm text-gray-600">Most popular formats</p>
              </div>
            </div>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'PDF', value: 45 },
                      { name: 'Excel', value: 30 },
                      { name: 'PowerPoint', value: 15 },
                      { name: 'HTML', value: 10 }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Quick Actions + Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              {[
                { label: 'Create from Template', icon: '📋', action: () => setActiveTab('templates'), color: 'from-purple-500 to-purple-600' },
                { label: 'Build Custom Report', icon: '🔧', action: () => setActiveTab('builder'), color: 'from-orange-500 to-orange-600' },
                { label: 'Schedule Report', icon: '⏰', action: () => { setModalType('schedule'); setShowModal(true); }, color: 'from-green-500 to-green-600' },
                { label: 'View Analytics', icon: '📈', action: () => setActiveTab('analytics'), color: 'from-pink-500 to-pink-600' }
              ].map((action, idx) => (
                <button
                  key={idx}
                  onClick={action.action}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:shadow-md transition-all group"
                >
                  <div className={`bg-gradient-to-br ${action.color} p-2 rounded-lg group-hover:scale-110 transition-transform`}>
                    <span className="text-xl">{action.icon}</span>
                  </div>
                  <span className="font-medium text-gray-800 text-left">{action.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Recent Reports */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Recent Reports</h3>
              <button 
                onClick={() => setActiveTab('my-reports')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View All →
              </button>
            </div>
            <div className="space-y-3">
              {customReports.slice(0, 5).map((report, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-lg">
                      <span className="text-xl">📄</span>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 text-sm">{report.name}</h4>
                      <p className="text-xs text-gray-600">{report.framework} • {report.category}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      report.status === 'Published' ? 'bg-green-100 text-green-700' :
                      report.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {report.status}
                    </span>
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">View</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tips & Insights */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="bg-blue-500 p-3 rounded-xl">
              <span className="text-2xl">💡</span>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-blue-900 mb-2">Pro Tip</h3>
              <p className="text-blue-800 text-sm mb-3">
                Save time by creating report templates for recurring reports. Templates can be scheduled for automatic generation and distribution.
              </p>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all">
                Learn More →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderTemplates() {
    const filteredTemplates = reportTemplates.filter(template => {
      const matchesFramework = !filters.framework || template.framework === filters.framework;
      const matchesSearch = !searchQuery || template.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFramework && matchesSearch;
    });

    return (
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4">
          <select 
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={filters.framework}
            onChange={(e) => setFilters({...filters, framework: e.target.value})}
          >
            <option value="">All Frameworks</option>
            <option value="GRI">GRI Standards</option>
            <option value="SASB">SASB</option>
            <option value="TCFD">TCFD</option>
            <option value="CDP">CDP</option>
            <option value="EU Taxonomy">EU Taxonomy</option>
          </select>
          <select 
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onChange={(e) => setFilters({...filters, complexity: e.target.value})}
          >
            <option value="">All Complexity Levels</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
          <div className="ml-auto text-sm text-gray-600">
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {/* Template Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all group">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                      {template.framework}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      template.complexity === 'High' ? 'bg-red-100 text-red-700' :
                      template.complexity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {template.complexity}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-yellow-500">
                  <span>⭐</span>
                  <span className="text-sm font-semibold text-gray-700">{template.rating}</span>
                </div>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {template.name}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2">{template.description}</p>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-gray-900">{template.sections.length}</div>
                  <div className="text-xs text-gray-600">Sections</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-gray-900">{template.estimatedTime}</div>
                  <div className="text-xs text-gray-600">Est. Time</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-lg font-bold text-gray-900">{template.usageCount}</div>
                  <div className="text-xs text-gray-600">Used</div>
                </div>
              </div>

              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    const newReport = esgCustomReportsService.createReportFromTemplate(template.id, {
                      name: `${template.name} - ${new Date().toLocaleDateString()}`,
                      createdBy: 'Current User'
                    });
                    if (newReport) {
                      setSelectedReport(newReport);
                      setActiveTab('my-reports');
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-all shadow-md"
                >
                  Use Template
                </button>
                <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                  Preview
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderMyReports() {
    const filteredReports = customReports.filter(report => {
      const matchesStatus = !filters.status || report.status === filters.status;
      const matchesSearch = !searchQuery || report.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesSearch;
    });

    return (
      <div className="space-y-6">
        {/* Filters & Actions */}
        <div className="flex flex-wrap items-center gap-4">
          <select 
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
          >
            <option value="">All Status</option>
            <option value="Draft">Draft</option>
            <option value="In Progress">In Progress</option>
            <option value="Published">Published</option>
          </select>
          <select 
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500"
            onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
          >
            <option value="recent">Most Recent</option>
            <option value="name">Name (A-Z)</option>
            <option value="framework">Framework</option>
          </select>
          <div className="ml-auto text-sm text-gray-600">
            {filteredReports.length} report{filteredReports.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Report</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Framework</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Modified</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.map((report, idx) => (
                  <tr key={idx} className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-2 rounded-lg">
                          <span className="text-lg">📄</span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{report.name}</div>
                          <div className="text-xs text-gray-500">{report.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 inline-flex text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                        {report.framework}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-700">{report.category}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                        report.status === 'Published' ? 'bg-green-100 text-green-700' :
                        report.status === 'In Progress' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {report.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(report.lastModified).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">View</button>
                        <button className="text-green-600 hover:text-green-700 text-sm font-medium">Edit</button>
                        <button 
                          onClick={() => {
                            esgCustomReportsService.generateReport(report.id);
                          }}
                          className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                        >
                          Generate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  function renderBuilder() {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-200">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🔧</span>
            <h2 className="text-2xl font-bold text-gray-900">Report Builder</h2>
          </div>
          <p className="text-gray-700">Drag and drop interface to create custom ESG reports with your choice of data sources, metrics, and visualizations.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Data Sources */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>🗄️</span>
              <span>Data Sources</span>
            </h3>
            <div className="space-y-3">
              {reportBuilder.availableDataSources?.map((source, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all cursor-move">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-gray-800 text-sm">{source.name}</h4>
                    <span className={`w-2 h-2 rounded-full ${
                      source.dataQuality > 95 ? 'bg-green-500' : 
                      source.dataQuality > 90 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></span>
                  </div>
                  <p className="text-xs text-gray-600 mb-2">{source.description}</p>
                  <div className="text-xs text-gray-500">
                    {source.recordCount?.toLocaleString()} records
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Metrics */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>📊</span>
              <span>Metrics</span>
            </h3>
            <div className="space-y-4">
              {reportBuilder.availableMetrics?.map((category, idx) => (
                <div key={idx}>
                  <h4 className="font-semibold text-gray-700 text-sm mb-2">{category.category}</h4>
                  <div className="space-y-2">
                    {category.metrics.slice(0, 3).map((metric, midx) => (
                      <div key={midx} className="flex items-center justify-between p-2 border border-gray-200 rounded-lg hover:bg-blue-50 cursor-move transition-colors">
                        <span className="text-xs text-gray-800">{metric.name}</span>
                        <span className="text-xs text-gray-500">{metric.unit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Visualizations */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>📈</span>
              <span>Charts</span>
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {reportBuilder.visualizationOptions?.map((viz, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all cursor-move text-center">
                  <div className="text-2xl mb-1">
                    {viz.type === 'line_chart' ? '📈' :
                     viz.type === 'bar_chart' ? '📊' :
                     viz.type === 'pie_chart' ? '🥧' :
                     viz.type === 'scatter_plot' ? '🔵' :
                     viz.type === 'heatmap' ? '🔥' :
                     viz.type === 'gauge_chart' ? '⏱️' :
                     '📊'}
                  </div>
                  <div className="text-xs font-medium text-gray-800">{viz.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Report Canvas */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>🎨</span>
              <span>Canvas</span>
            </h3>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center min-h-[400px] flex flex-col items-center justify-center">
              <div className="text-5xl mb-4">📄</div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">Drop Elements Here</h4>
              <p className="text-sm text-gray-600 mb-4">Drag data sources, metrics, and charts to build your report</p>
              <button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-6 py-2 rounded-lg font-medium text-sm transition-all shadow-md">
                Start Building
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderAnalytics() {
    return (
      <div className="space-y-6">
        {/* Usage Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: 'Reports Generated', value: reportAnalytics.usage?.totalReportsGenerated || 0, icon: '📊', color: 'from-blue-500 to-blue-600' },
            { label: 'Total Downloads', value: reportAnalytics.usage?.totalDownloads || 0, icon: '📥', color: 'from-green-500 to-green-600' },
            { label: 'Avg Gen Time', value: reportAnalytics.usage?.avgGenerationTime || '0s', icon: '⚡', color: 'from-purple-500 to-purple-600' },
            { label: 'User Rating', value: `${reportAnalytics.performance?.userSatisfaction || 0}/5`, icon: '⭐', color: 'from-yellow-500 to-yellow-600' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className={`bg-gradient-to-br ${stat.color} p-3 rounded-xl w-fit mb-3`}>
                <span className="text-2xl">{stat.icon}</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Template Adoption */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Template Adoption Rate</h3>
            <div className="space-y-4">
              {Object.entries(reportAnalytics.trends?.templateAdoption || {
                'GRI Standards': 35,
                'SASB': 28,
                'TCFD': 22,
                'CDP': 15
              }).map(([template, percentage]) => (
                <div key={template}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">{template}</span>
                    <span className="font-bold text-gray-900">{percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Format Preferences */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Export Format Preferences</h3>
            <div className="space-y-4">
              {Object.entries(reportAnalytics.trends?.formatPreferences || {
                'PDF': 45,
                'Excel': 30,
                'PowerPoint': 15,
                'HTML': 10
              }).map(([format, percentage]) => (
                <div key={format}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-medium text-gray-700">{format}</span>
                    <span className="font-bold text-gray-900">{percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-emerald-600 h-3 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Performance Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Success Rate', value: `${reportAnalytics.performance?.successRate || 98}%`, color: 'text-green-600' },
              { label: 'Avg File Size', value: reportAnalytics.performance?.avgFileSize || '2.3MB', color: 'text-blue-600' },
              { label: 'Storage Used', value: reportAnalytics.performance?.storageUsed || '45GB', color: 'text-purple-600' },
              { label: 'Error Rate', value: `${reportAnalytics.performance?.errorRate || 2}%`, color: 'text-red-600' }
            ].map((metric, idx) => (
              <div key={idx} className="text-center p-4 rounded-xl bg-gray-50">
                <div className={`text-3xl font-bold ${metric.color} mb-1`}>{metric.value}</div>
                <div className="text-sm text-gray-600">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderCollaboration() {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Shared Reports */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>📤</span>
              <span>Shared Reports</span>
            </h3>
            <div className="space-y-3">
              {collaboration.sharedReports?.map((share, idx) => (
                <div key={idx} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-gray-900">Report #{share.reportId}</span>
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      <span>👁️</span>
                      <span>{share.accessCount} views</span>
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    Shared by <span className="font-medium">{share.sharedBy}</span> with <span className="font-medium">{share.sharedWith.length}</span> users
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {share.permissions.map((perm, pidx) => (
                      <span key={pidx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {perm}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Comments */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>💬</span>
              <span>Recent Comments</span>
            </h3>
            <div className="space-y-3">
              {collaboration.comments?.map((comment, idx) => (
                <div key={idx} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{comment.author}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(comment.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-xs text-blue-600 mb-2">Section: {comment.section}</div>
                  <div className="text-sm text-gray-700 mb-3">{comment.content}</div>
                  <div className="flex items-center gap-3">
                    <button className="text-blue-600 hover:text-blue-700 text-xs font-medium">Reply</button>
                    {!comment.resolved && (
                      <button className="text-green-600 hover:text-green-700 text-xs font-medium">✓ Resolve</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Share New Report */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>➕</span>
              <span>Share Report</span>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Report</label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  {customReports.map((report, idx) => (
                    <option key={idx} value={report.id}>{report.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Recipients</label>
                <textarea 
                  placeholder="Enter email addresses separated by commas"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Permissions</label>
                <div className="space-y-2">
                  {['View', 'Comment', 'Export', 'Edit'].map((perm, idx) => (
                    <label key={idx} className="flex items-center gap-2">
                      <input type="checkbox" className="rounded" defaultChecked={perm === 'View'} />
                      <span className="text-sm text-gray-700">{perm}</span>
                    </label>
                  ))}
                </div>
              </div>
              <button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-md">
                Share Report
              </button>
            </div>
          </div>

          {/* Pending Approvals */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>✅</span>
              <span>Pending Approvals</span>
            </h3>
            <div className="space-y-3">
              {collaboration.approvals?.map((approval, idx) => (
                <div key={idx} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">Report #{approval.reportId}</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      approval.status === 'Approved' ? 'bg-green-100 text-green-700' :
                      approval.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {approval.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">Approver: {approval.approver}</div>
                  {approval.comments && (
                    <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{approval.comments}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderModal() {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {modalType === 'create' ? 'Create New Report' : 'Schedule Report'}
            </h2>
            <button 
              onClick={() => setShowModal(false)}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>
          <div className="p-6">
            <p className="text-gray-600">Modal content for {modalType}...</p>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions
  function generateActivityData() {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => ({
      day,
      reports: Math.floor(Math.random() * 50) + 20
    }));
  }
};

export default ESGCustomReports;
